import React, { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppConfigProvider } from './context/AppConfigContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { ThemeModeProvider, useThemeMode } from './context/ThemeContext';

// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import VehicleSearch from './pages/VehicleSearch';
import VehicleDetail from './pages/VehicleDetail';
import SavedSearches from './pages/SavedSearches';
import Profile from './pages/Profile';
import Subscription from './pages/Subscription';
import Favorites from './pages/Favorites';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import CockpitShell from './pages/CockpitShell';

// Components
import Layout from './components/Layout';

// Build theme based on mode
function buildTheme(mode) {
  const isDark = mode === 'dark';
  const blue = isDark ? '#60a5fa' : '#046AFF';
  const blueHover = isDark ? '#93bbfc' : '#0355d6';
  const blueAlpha = isDark ? 'rgba(96, 165, 250, 0.14)' : 'rgba(4, 106, 255, 0.14)';
  const blueBorder = isDark ? 'rgba(96, 165, 250, 0.3)' : 'rgba(4, 106, 255, 0.3)';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? '#ffffff' : '#0a0a0a',
        light: isDark ? '#e0e0e0' : '#404040',
        dark: isDark ? '#ffffff' : '#000000',
        contrastText: isDark ? '#0A0E17' : '#ffffff',
      },
      secondary: {
        main: isDark ? '#60a5fa' : '#046AFF',
        light: '#60a5fa',
        dark: '#0355d6',
      },
      background: {
        default: isDark ? '#0A0E17' : '#fafafa',
        paper: isDark ? '#111827' : '#ffffff',
      },
      text: {
        primary: isDark ? '#F1F5F9' : '#0a0a0a',
        secondary: isDark ? '#9ca3af' : '#6b7280',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h6: {
        fontWeight: 600,
        letterSpacing: '-0.025em',
      },
      body2: {
        color: isDark ? '#9ca3af' : '#6b7280',
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: '6px 16px',
            fontSize: '0.875rem',
            transition: 'all 150ms ease',
          },
          contained: {
            backgroundColor: blue,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: blueHover,
            },
          },
          text: {
            color: blue,
            '&:hover': {
              backgroundColor: blueAlpha,
            },
          },
          outlined: {
            color: blue,
            borderColor: blueBorder,
            '&:hover': {
              borderColor: blue,
              backgroundColor: blueAlpha,
            },
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(10, 10, 10, 0.8)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            color: isDark ? '#F1F5F9' : '#0a0a0a',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            border: 'none',
            boxShadow: isDark
              ? '0 0 0 1px rgba(255,255,255,0.06), 0 8px 40px rgba(0,0,0,0.4)'
              : '0 0 0 1px rgba(0,0,0,0.08), 0 8px 40px rgba(0,0,0,0.12)',
          },
        },
      },
      MuiListItemButton: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: '2px 8px',
            padding: '8px 12px',
            transition: 'all 150ms ease',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
              },
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: isDark
              ? '0 0 0 1px rgba(255,255,255,0.06), 0 4px 24px rgba(0,0,0,0.4)'
              : '0 0 0 1px rgba(0,0,0,0.08), 0 4px 24px rgba(0,0,0,0.12)',
            borderRadius: 8,
            marginTop: 4,
          },
        },
      },
      MuiMenuItem: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            transition: 'all 150ms ease',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: isDark ? {
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
            },
          } : {},
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: isDark ? {
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
          } : {},
        },
      },
      MuiChip: {
        styleOverrides: {
          root: isDark ? {
            borderColor: 'rgba(255,255,255,0.15)',
          } : {},
        },
      },
    },
  });
}

// Route guard for features requiring a real (non-anonymous) account
const AuthenticatedRoute = ({ children }) => {
  const { user, isAnonymous, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user || isAnonymous) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Default landing varies by role: dealers + enthusiasts see the cockpit;
// consumers (and anonymous browsers) get redirected to manual browse.
// Legacy users without a `role` field default to 'consumer'.
const RoleAwareLanding = () => {
  const { userData, isAnonymous } = useAuth();
  const role = isAnonymous ? 'consumer' : (userData?.role || 'consumer');
  if (role === 'dealer' || role === 'enthusiast') return <CockpitShell />;
  return <Navigate to="/browse" replace />;
};

// Loading screen while auth initializes (prevents flash of login page)
const AppContent = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* Layout wraps all app routes — accessible to everyone */}
        <Route path="/" element={<Layout />}>
          {/* Public routes — anonymous users can access */}
          <Route index element={<RoleAwareLanding />} />
          <Route path="browse" element={<VehicleSearch />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="vehicle/:id" element={<VehicleDetail />} />
          <Route path="favorites" element={<Favorites />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<TermsOfUse />} />

          {/* Auth-gated routes — require real (non-anonymous) account */}
          <Route path="saved-searches" element={
            <AuthenticatedRoute><SavedSearches /></AuthenticatedRoute>
          } />
          <Route path="profile" element={
            <AuthenticatedRoute><Profile /></AuthenticatedRoute>
          } />
          <Route path="subscription" element={
            <AuthenticatedRoute><Subscription /></AuthenticatedRoute>
          } />
        </Route>

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

// Themed app wrapper — reads mode from ThemeContext
const ThemedApp = () => {
  const { mode } = useThemeMode();
  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppConfigProvider>
          <FavoritesProvider>
            <AppContent />
          </FavoritesProvider>
        </AppConfigProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <ThemeModeProvider>
      <ThemedApp />
    </ThemeModeProvider>
  );
}

export default App;
